import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type SyntheticEvent,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_WORDING_BODY,
  MAX_WORDING_SUBJECT,
  WORDING_CATALOG,
  WORDING_KINDS,
  can,
  type Wording,
  type WordingKind,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { useSession } from '../session';
import { DangerAction, EditSheet } from '../ui/EditSheet';
import { Button } from '../ui/Button';
import { Notice, StatusBadge } from '../ui/Badge';
import { Field, field } from '../create/Field';
import {
  awaitingServer,
  refreshFailed,
  shownState,
  StaleNotice,
  unresolved,
  UnresolvedState,
} from '../ui/QueryStates';
import { useDebounced } from '../ui/useDebounced';
import { IconMail } from '../icons';
import {
  charCount,
  chipsFor,
  endOfMessage,
  exampleLine,
  insertField,
  type Caret,
  type WordingPart,
} from './wordingView';

/**
 * Edit wording (MAP 249 spec §6, §9; ticket 05): one customer email kind's subject and message,
 * its field chips, and a live preview the SERVER renders with the send's own function on the
 * shop's latest real record of the kind (or a sample). "Reset to ours" deletes the shop's own.
 *
 * Self-contained: Settings → Email (ticket 07) mounts `EditWordingButton` per kind in its list of
 * automatic emails (`EditWordingSlot`). The owner edits (`org:manage`); the office reads the
 * wording and its preview (spec §13).
 *
 * Thin by design: nothing here renders or judges the words. The preview and every refusal are the
 * server's (`emailWording.preview` / `.set`), so the sheet cannot promise an email the send would
 * not write.
 */

/** The server's preview (`services/email-wording.ts` `WordingPreview`). */
export interface WordingPreviewData {
  subject: string;
  body: string;
  footer: string | null;
  example: { sample: boolean; label: string };
  replies: boolean;
  problem: string | null;
}

/** How long typing must pause before the preview is asked for again. */
const PREVIEW_DEBOUNCE_MS = 300;

const textArea =
  'min-h-48 w-full resize-y rounded-[var(--radius)] border border-border bg-surface p-2 text-sm leading-relaxed';

export function EmailWordingSheet({
  kind,
  open,
  onClose,
}: {
  kind: WordingKind;
  open: boolean;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const mayEdit = can(useSession().role, 'org', 'manage');
  const info = WORDING_CATALOG[kind];
  const saved = useQuery({
    ...trpc.emailWording.get.queryOptions({ kind }),
    enabled: open,
  });

  /** What the owner typed; null = nothing yet, the saved wording shows. */
  const [draft, setDraft] = useState<Wording | null>(null);
  /** Where the owner was last (a field chip goes there). */
  const caret = useRef<Caret | null>(null);
  const subjectBox = useRef<HTMLInputElement>(null);
  const messageBox = useRef<HTMLTextAreaElement>(null);
  /** A caret to put back once React has drawn the inserted field. */
  const [refocus, setRefocus] = useState<Caret | null>(null);
  const [preview, setPreview] = useState<WordingPreviewData | null>(null);

  // A sheet kept mounted between uses opens on what is saved, never on last time's typing.
  useEffect(() => {
    if (open) return;
    setDraft(null);
    setPreview(null);
    caret.current = null;
  }, [open]);

  const base: Wording | null = saved.data
    ? { subject: saved.data.subject, body: saved.data.body }
    : null;
  const shown = draft ?? base;
  const dirty =
    draft !== null &&
    base !== null &&
    (draft.subject !== base.subject || draft.body !== base.body);

  /** The server's answer is the wording now: no refetch flashes the old text back. */
  const landed = (view: NonNullable<typeof saved.data>) => {
    qc.setQueryData(trpc.emailWording.get.queryKey({ kind }), view);
    void qc.invalidateQueries({ queryKey: trpc.emailWording.list.queryKey() });
    setDraft(null);
  };
  const save = useMutation(
    trpc.emailWording.set.mutationOptions({
      onSuccess: (view) => {
        landed(view);
        onClose();
      },
    }),
  );
  const reset = useMutation(
    trpc.emailWording.reset.mutationOptions({ onSuccess: landed }),
  );

  // The preview: asked of the server a beat after typing stops, always for the text on screen.
  const ask = useMutation(trpc.emailWording.preview.mutationOptions());
  const subject = useDebounced(shown?.subject ?? null, PREVIEW_DEBOUNCE_MS);
  const body = useDebounced(shown?.body ?? null, PREVIEW_DEBOUNCE_MS);
  const askPreview = ask.mutate;
  useEffect(() => {
    if (!open || subject === null || body === null) return;
    // Only the LAST ask's answer lands (TanStack calls a `mutate` callback for the latest call).
    askPreview({ kind, draft: { subject, body } }, { onSuccess: setPreview });
  }, [open, kind, subject, body, askPreview]);

  useEffect(() => {
    if (!refocus) return;
    const box = refocus.part === 'subject' ? subjectBox.current : messageBox.current;
    box?.focus();
    box?.setSelectionRange(refocus.start, refocus.end);
    setRefocus(null);
  }, [refocus]);

  const remember =
    (part: WordingPart) =>
    (e: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const box = e.currentTarget;
      caret.current = {
        part,
        start: box.selectionStart ?? box.value.length,
        end: box.selectionEnd ?? box.value.length,
      };
    };
  const change =
    (part: WordingPart) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      // The updater form: two changes in one render must not drop the first (LESSONS 10-06).
      setDraft((d) => ({ ...(d ?? base!), [part]: value }));
      if (save.error) save.reset();
    };
  const addField = (name: Parameters<typeof insertField>[2]) => {
    if (!shown) return;
    const next = insertField(shown, caret.current ?? endOfMessage(shown), name);
    setDraft(next.wording);
    caret.current = next.caret;
    setRefocus(next.caret);
  };

  return (
    <EditSheet
      open={open}
      onClose={onClose}
      title={`${info.label} email`}
      wide
      onSave={() => {
        if (draft) save.mutate({ kind, ...draft, ...idem() });
      }}
      saving={awaitingServer(save)}
      error={save.error ?? reset.error}
      dirty={mayEdit && dirty}
      destructive={
        mayEdit && saved.data?.edited ? (
          <DangerAction
            label="Reset to ours"
            question={`Go back to ServWell’s wording for the ${info.label.toLowerCase()} email?`}
            detail="Your wording for this email is deleted. Emails already on their way keep the words they were written with."
            onConfirm={() => reset.mutate({ kind, ...idem() })}
            pending={awaitingServer(reset)}
          />
        ) : null
      }
    >
      <p className="text-sm text-muted-foreground">{info.timing}</p>
      {/* The words below may be older than what is saved: say so (042). */}
      <StaleNotice
        show={refreshFailed(saved, saved.data)}
        onRetry={() => void saved.refetch()}
      />
      {!mayEdit ? (
        <Notice tone="info">Only the owner can change this wording.</Notice>
      ) : null}
      {!shown ? (
        <UnresolvedState
          state={unresolved(shownState(saved, saved.data))}
          what="this email’s wording"
          icon={IconMail}
          error={saved.error}
          onRetry={() => void saved.refetch()}
        />
      ) : (
        <>
          <Field label="Subject">
            <input
              ref={subjectBox}
              className={field}
              value={shown.subject}
              readOnly={!mayEdit}
              onChange={change('subject')}
              onSelect={remember('subject')}
              onBlur={remember('subject')}
            />
          </Field>
          <Counter text={shown.subject} max={MAX_WORDING_SUBJECT} />
          <Field label="Message">
            <textarea
              ref={messageBox}
              className={textArea}
              rows={12}
              value={shown.body}
              readOnly={!mayEdit}
              onChange={change('body')}
              onSelect={remember('body')}
              onBlur={remember('body')}
            />
          </Field>
          <Counter text={shown.body} max={MAX_WORDING_BODY} />
          {mayEdit ? <FieldChips kind={kind} onAdd={addField} /> : null}
          <p className="text-xs text-muted-foreground">
            A field in {'{{ }}'} is filled in for each email. Text in [square brackets] is
            left out when a field in it is empty, like [, due {'{{due_date}}'}]. ServWell
            adds the link and the footer: there is no need to type a web address, a phone
            number or an email address, and the wording can’t hold one.
          </p>
          <WordingPreviewView preview={preview} />
        </>
      )}
    </EditSheet>
  );
}

/** "123 / 2,000", in the warning tone past the limit. */
function Counter({ text, max }: { text: string; max: number }) {
  const n = charCount(text);
  return (
    <p
      className={`-mt-2 text-right text-xs ${n > max ? 'text-danger' : 'text-muted-foreground'}`}
    >
      {n.toLocaleString('en-US')} / {max.toLocaleString('en-US')}
    </p>
  );
}

/** The kind's fields, as buttons that put one in where the owner was. */
export function FieldChips({
  kind,
  onAdd,
}: {
  kind: WordingKind;
  onAdd: (name: ReturnType<typeof chipsFor>[number]['name']) => void;
}) {
  return (
    <div role="group" aria-label="Fields you can add" className="flex flex-wrap gap-1.5">
      {chipsFor(kind).map((chip) => (
        <Button
          key={chip.name}
          size="sm"
          variant="ghost"
          title={chip.label}
          aria-label={`Add ${chip.token}: ${chip.label}`}
          onClick={() => onAdd(chip.name)}
        >
          {chip.token}
        </Button>
      ))}
    </div>
  );
}

/** The server's preview: the subject, the message and the footer, as the customer gets them. */
export function WordingPreviewView({ preview }: { preview: WordingPreviewData | null }) {
  return (
    <section className="space-y-2 border-t border-border pt-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        What your customer gets
      </h3>
      {!preview ? (
        <p className="text-sm text-muted-foreground">Getting the preview…</p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">{exampleLine(preview.example)}</p>
          {preview.problem ? (
            <Notice tone="warn">This can’t be saved yet: {preview.problem}</Notice>
          ) : null}
          <div className="rounded-[var(--radius)] border border-border bg-surface-2 p-3 text-sm">
            <p className="font-semibold [overflow-wrap:anywhere]">{preview.subject}</p>
            <div className="mt-2 whitespace-pre-wrap [overflow-wrap:anywhere]">
              {preview.body}
            </div>
            {preview.footer ? (
              <div className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground [overflow-wrap:anywhere]">
                {preview.footer.replace(/^\n/, '')}
              </div>
            ) : null}
          </div>
          {!preview.replies ? (
            <p className="text-xs text-muted-foreground">
              Replies to these emails don’t reach you yet, so {'{{reply_line}}'} asks your
              customer to contact you directly. Once a reply address is confirmed, it asks
              them to reply.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

/** One kind's "Edit wording" (the owner) or "See wording" (the office), with its sheet. */
export function EditWordingButton({ kind }: { kind: WordingKind }) {
  const [open, setOpen] = useState(false);
  const mayEdit = can(useSession().role, 'org', 'manage');
  const label = WORDING_CATALOG[kind].label.toLowerCase();
  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        aria-haspopup="dialog"
        aria-label={`${mayEdit ? 'Edit' : 'See'} the wording of the ${label} email`}
        onClick={() => setOpen(true)}
      >
        {mayEdit ? 'Edit wording' : 'See wording'}
      </Button>
      <EmailWordingSheet kind={kind} open={open} onClose={() => setOpen(false)} />
    </>
  );
}
